import { HistoricalDataAlert } from './GeneralInput.styles';
import { Box, Grid, Tooltip, Typography, SelectChangeEvent } from '@mui/material';
import {
  ControlBar,
  IControlButton,
  InfoBlock,
  MainContainer,
  ConfirmationModal,
  FormItemLabel,
  FormGrid,
  Loader,
  IFieldValues,
  useNotification,
} from '../../components';
import { generatePath, useNavigate } from 'react-router';
import URLs from '../URLs';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSalonStore } from '../../store';
import InfoIcon from '@mui/icons-material/Info';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import { BASE_FIELD_BACKGROUND_COLOR } from '../../themes/tokens';

import DateRangesBlock, { Quarters } from './DateRangesBlock';
import HistoricalDataModal from './HistoricalData/HistoricalDataModal';
import {
  ISalonInputFormItem,
  SalonInfoMap,
  SalonInputFormMap,
  SalonInputTitleDescription,
  selectableKPIs,
} from '../../types';
import { getKpiFieldConfig } from '../../utils/kpiFieldUtils';
import { KpiTitle, RequiredKpiTitles } from '../../types/SalonKpiData';
import { getKpiTitleForDbKey } from '../../types/SalonInputData'; // <-- add this import

import { useTranslation } from 'react-i18next';
import { filterAndRenameObjectKeys } from '../../utils';
import { MenuItem, Select, FormControl, InputLabel } from '@mui/material';

const mapCYToFYPeriod = (cYear: string, cQuarter: number): Record<string, string> => {
  let fYear = parseInt(cYear);
  let fQuarter = cQuarter + 2;

  if (fQuarter > 3) {
    fQuarter -= 4;
    fYear += 1;
  } else if (fQuarter < 0) {
    fQuarter += 4;
    fYear -= 1;
  }

  return {
    'fiscal Year': `FY${fYear.toString()}`,
    'fiscal Quarter': Quarters[fQuarter]?.split(':')?.[0],
  };
};
const GeneralInput = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [totalValues, setTotalValues] = useState({ tnTransactions: 0, totalSalonSales: 0 });
  const { salonId, planId, year, quarter } = useParams();
  const {
    isLoading,
    salon,
    fetchSalonInputData,
    salonInputData,
    updateSalonInputData,
    fetchSalon,
    getDbKeyForKpiTitle,
  } = useSalonStore();
  const [isHistoricalModalOpen, setIsHistoricalModalOpen] = useState(false);

  const [calendarPeriod, setCalendarPeriod] = useState<[string, number]>([
    year || new Date().getFullYear().toString(),
    Number(quarter) - 1 || 0,
  ]);
  const changePeriod = (cYear: string, cQuarter: number): void => {
    setCalendarPeriod([cYear, cQuarter]);
  };

  const { showMessage } = useNotification();

  const [selectedKPIs, setSelectedKPIs] = useState<KpiTitle[]>([]);

  const [currentValues, setCurrentValues] = useState<IFieldValues>({});
  const [dataInputs, setDataInputs] = useState(SalonInputFormMap);

  const handleKPIChange = (event: SelectChangeEvent<KpiTitle[]>) => {
    const newSelectedKPIs = event.target.value as KpiTitle[];
    const addedKPIs = newSelectedKPIs.filter((kpi) => !selectedKPIs.includes(kpi));
    const removedKPIs = selectedKPIs.filter((kpi) => !newSelectedKPIs.includes(kpi));
    const updatedDataInputs: typeof SalonInputFormMap = {
      staff: dataInputs.staff.map((item) => ({ ...item })),
      guest: dataInputs.guest.map((item) => ({ ...item })),
      service: dataInputs.service.map((item) => ({ ...item })),
      retail: dataInputs.retail.map((item) => ({ ...item })),
    };
    const updateFrequency = (inputIds: string[], delta: number) => {
      Object.entries(updatedDataInputs).forEach(([_, items]) => {
        items.forEach((item) => {
          if (inputIds.includes(item.id)) item.frequency = Math.max(0, item.frequency + delta);
        });
      });
    };
    addedKPIs.forEach((kpi) => {
      const inputIds = selectableKPIs[kpi] || [];
      updateFrequency(inputIds, -1);
    });
    removedKPIs.forEach((kpi) => {
      const inputIds = selectableKPIs[kpi] || [];
      updateFrequency(inputIds, +1);
    });
    setSelectedKPIs(newSelectedKPIs);
    setDataInputs(updatedDataInputs);

    // Update currentValues.kpi to only include selected KPIs
    setCurrentValues((prev) => {
      const prevKpi = prev.kpi || {};
      // Remove keys for unselected KPIs (this clears their values)
      const filteredKpi = Object.fromEntries(
        Object.entries(prevKpi).filter(([k]) => newSelectedKPIs.includes(k as KpiTitle)),
      );
      // Add keys for newly added KPIs (with empty string if not present)
      addedKPIs.forEach((kpi) => {
        if (!(kpi in filteredKpi)) {
          filteredKpi[kpi] = '';
        }
      });
      return {
        ...prev,
        kpi: filteredKpi,
      };
    });
  };

  useEffect(() => {
    if (salonId && !salon) {
      fetchSalon(salonId, showMessage, navigate);
    }
  }, [salon, salonId]);

  useEffect(() => {
    if (salonId) {
      fetchSalonInputData(salonId, calendarPeriod[0], calendarPeriod[1] + 1, showMessage, navigate);
    }
  }, [salonId, calendarPeriod]);

  // Consolidated state management for form data
  useEffect(() => {
    if (salonInputData?.kpi) {
      // 1. Process KPI data and update selectedKPIs
      const selectedTitles = Object.entries(salonInputData.kpi)
        .filter(([_, value]) => value !== null && value !== undefined)
        .map(([dbKey]) => getKpiTitleForDbKey(dbKey))
        .filter(Boolean) as KpiTitle[];

      // 2. Convert KPI data to use proper keys for the form
      const convertedKpi = Object.fromEntries(
        Object.entries(salonInputData.kpi).map(([dbKey, value]) => {
          const kpiTitle = getKpiTitleForDbKey(dbKey);
          return [kpiTitle ?? dbKey, value];
        }),
      );

      // 3. Update all form values at once
      setCurrentValues({
        ...salonInputData,
        kpi: convertedKpi,
      });

      // 4. Update KPI selections
      setSelectedKPIs(selectedTitles);

      // 5. Update input field frequencies based on selected KPIs
      const updatedDataInputs: typeof SalonInputFormMap = {
        staff: SalonInputFormMap.staff.map((item) => ({ ...item })),
        guest: SalonInputFormMap.guest.map((item) => ({ ...item })),
        service: SalonInputFormMap.service.map((item) => ({ ...item })),
        retail: SalonInputFormMap.retail.map((item) => ({ ...item })),
      };

      // Reset all frequencies to their original values first
      Object.values(selectableKPIs).forEach((inputIds) => {
        Object.entries(updatedDataInputs).forEach(([_, items]) => {
          items.forEach((item) => {
            if (inputIds.includes(item.id)) {
              // Find original frequency from SalonInputFormMap
              const originalItem = Object.values(SalonInputFormMap)
                .flat()
                .find((f) => f.id === item.id);
              if (originalItem) item.frequency = originalItem.frequency;
            }
          });
        });
      });

      // Then decrease frequencies for selected KPIs
      selectedTitles.forEach((kpi) => {
        const inputIds = selectableKPIs[kpi] || [];
        Object.entries(updatedDataInputs).forEach(([_, items]) => {
          items.forEach((item) => {
            if (inputIds.includes(item.id)) item.frequency = Math.max(0, item.frequency - 1);
          });
        });
      });

      setDataInputs(updatedDataInputs);
    } else {
      setSelectedKPIs([]);
      setCurrentValues({});
      setDataInputs(SalonInputFormMap); // Reset to original state
    }
  }, [salonInputData]);

  // Improved total calculation with specific dependencies
  useEffect(() => {
    const retailOnlyTr = Number(currentValues?.tnRetailOnlyTr) || 0;
    const serviceTrans = Number(currentValues?.tnServiceTransactions) || 0;
    const retailSales = Number(currentValues?.totalRetailSales) || 0;
    const serviceSales = Number(currentValues?.totalServiceSales) || 0;

    setTotalValues({
      tnTransactions: retailOnlyTr + serviceTrans,
      totalSalonSales: retailSales + serviceSales,
    });
  }, [
    currentValues?.tnRetailOnlyTr,
    currentValues?.tnServiceTransactions,
    currentValues?.totalRetailSales,
    currentValues?.totalServiceSales,
  ]);

  // Improved required fields detection
  const requiredFields = useMemo(() => {
    // Fields required by the form (excluding those covered by selected KPIs)
    const formRequiredFields = [...Object.values(dataInputs)]
      .flat()
      .filter((item: ISalonInputFormItem) => {
        if (!item.required || item.frequency <= 0) return false;

        // Check if this field is covered by any selected KPI
        const isCoveredByKpi = selectedKPIs.some((kpi) => {
          const kpiInputIds = selectableKPIs[kpi] || [];
          return kpiInputIds.includes(item.id);
        });

        // Only require the form field if it's not covered by a selected KPI
        return !isCoveredByKpi;
      })
      .map((item: ISalonInputFormItem) => item.id);

    // Return only form required fields (KPIs are handled separately in isInvalid)
    return formRequiredFields;
  }, [dataInputs, selectedKPIs]);

  // Updated isInvalid function to use the improved requiredFields logic
  const isInvalid = useMemo((): boolean => {
    if (!currentValues) return true;

    // Check if any required field is missing a value
    const missingRequiredFields = requiredFields.some((id) => {
      const value = currentValues?.[id];
      // Check for null, undefined, empty string, or just whitespace
      return !value || String(value).trim() === '';
    });

    // Check required KPIs are selected and have values
    const missingRequiredKPIs = RequiredKpiTitles.some(
      (kpi) =>
        selectedKPIs.includes(kpi) &&
        (!currentValues?.kpi?.[kpi] || String(currentValues?.kpi?.[kpi]).trim() === ''),
    );

    // Debug logging
    console.log('Required fields:', requiredFields);
    console.log('Current values:', currentValues);
    console.log('Selected KPIs:', selectedKPIs);

    // Detailed field checking
    const fieldDetails = requiredFields.map((id) => ({
      fieldId: id,
      value: currentValues?.[id],
      stringValue: String(currentValues?.[id]),
      trimmedValue: String(currentValues?.[id]).trim(),
      isEmpty: !currentValues?.[id] || String(currentValues?.[id]).trim() === '',
    }));
    console.log('Field details:', fieldDetails);

    console.log('Missing required fields:', missingRequiredFields);
    console.log('Missing required KPIs:', missingRequiredKPIs);

    return missingRequiredFields || missingRequiredKPIs;
  }, [currentValues, selectedKPIs, requiredFields]);

  const calculate = () => {
    // Create a new object with converted KPI keys instead of mutating currentValues
    const dataToSubmit = {
      ...currentValues,
      kpi: {
        ...Object.fromEntries(
          Object.entries(currentValues.kpi || {}).map(([k, v]) => [
            getDbKeyForKpiTitle(k as KpiTitle),
            v,
          ]),
        ),
      },
    };

    updateSalonInputData(salonId, calendarPeriod, dataToSubmit, showMessage, navigate, (data) => {
      navigate(
        generatePath(`${URLs.Salon}/${URLs.KpiResults}`, {
          salonId,
          inputId: data.id,
          year: data.year,
          quarter: data.quarter,
          planId: planId || null,
        }),
      );
    });
  };

  const handleModalOpen = () => setIsHistoricalModalOpen(true);
  const handleModalClose = () => setIsHistoricalModalOpen(false);

  const rightButtons: IControlButton[] = [
    { label: t('cancel'), action: () => setIsModalOpen(true) },
    {
      label: t('calculate'),
      action: calculate,
      disabled: isInvalid,
      variant: 'contained',
    },
  ];

  const totalFields = [
    {
      label: 'total # of transactions',
      id: 'tnTransactions',
      InputProps: {
        readOnly: true,
      },
    },
    {
      label: 'total salon sales',
      id: 'totalSalonSales',
      InputProps: {
        readOnly: true,
      },
    },
  ];

  return (
    <>
      <ControlBar
        backButtonAction={() => setIsModalOpen(true)}
        title={<Typography variant="h5">{salonInputData?.title || t('newPlan')}</Typography>}
        withKpiButton
      />
      <Loader isLoading={isLoading} />
      <MainContainer>
        <Typography variant="h6" textTransform={'uppercase'}>
          {salon?.title}
        </Typography>
        <InfoBlock
          infoList={filterAndRenameObjectKeys(salon, SalonInfoMap)}
          additionalInfo={mapCYToFYPeriod(calendarPeriod[0], calendarPeriod[1])}
        />
        <HistoricalDataAlert
          icon={
            <Tooltip
              title={
                "Please input data for the year preceding the last quarter. For example, if the quarter you're assessing is Q4 2024, then input historical data for 2023."
              }
              arrow
              placement={'top'}
            >
              <InfoIcon />
            </Tooltip>
          }
          action={
            <Button onClick={handleModalOpen} variant={'outlined'}>
              {t('inputHistoricalData')}
            </Button>
          }
        >
          {t('historicalData')}
        </HistoricalDataAlert>
        <Box mt={6}>
          <Typography variant="h6">
            <FormItemLabel
              label={'Date Range'}
              description={
                'Please specify the year and quarter for which you are conducting the Quarterly Business Review (QBR).'
              }
            />
          </Typography>
          <Grid container mt={1} spacing={2} data-testid="date-range-form">
            <DateRangesBlock
              year={calendarPeriod[0]}
              quarter={calendarPeriod[1]}
              onChange={changePeriod}
            />
          </Grid>
          {/* KPI section */}
          <Box mt={4}>
            <Typography variant="h6" textTransform={'capitalize'}>
              <FormItemLabel
                label={'KPIs'}
                description={'Please select the KPIs you would like to directly enter values for.'}
              />
            </Typography>
            <Box display="flex" flexDirection="row" gap={4} alignItems="flex-start">
              {/* KPI Selection Dropdown */}
              <Box mt={1} style={{ width: '49%' }}>
                <FormControl
                  style={{ backgroundColor: BASE_FIELD_BACKGROUND_COLOR }}
                  fullWidth
                  data-testid="kpi-select"
                >
                  <InputLabel id="kpi-select-label">{t('KPIs')}</InputLabel>
                  <Select
                    labelId="kpi-select-label"
                    id="kpi-select"
                    multiple
                    value={selectedKPIs}
                    onChange={handleKPIChange}
                    label={t('KPIs')}
                    renderValue={(selected) => (selected as KpiTitle[]).join(', ')}
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {Object.values(KpiTitle)
                      .filter((kpi) => kpi !== KpiTitle.tnTransactionsBmrk)
                      .map((kpi) => (
                        <MenuItem key={kpi} value={kpi}>
                          <Checkbox checked={selectedKPIs.includes(kpi as KpiTitle)} />
                          {kpi}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
              {/* Inputs for selected KPIs */}
              <Box flexGrow={1}>
                <FormGrid
                  key={`${salonId}-${planId}-${year}-${quarter}-${salonInputData?.id || 'new'}`}
                  formName=""
                  fields={selectedKPIs
                    .filter((kpi) => kpi !== KpiTitle.tnTransactionsBmrk)
                    .map(getKpiFieldConfig)}
                  values={currentValues?.kpi || {}} // 👈 controlled prop
                  onChangeFieldValues={(values) => {
                    setCurrentValues((prev) => ({
                      ...prev,
                      kpi: {
                        ...(prev?.kpi || {}),
                        ...(values as Record<string, string | number>),
                      },
                    }));
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        {Object.entries(dataInputs).map(([formName, fields]) => {
          const filteredFields = fields.filter(
            (field: { frequency: number }) => field.frequency > 0,
          );
          if (filteredFields.length === 0) return null;
          return (
            <FormGrid
              key={formName}
              formName={formName}
              formDescription={
                SalonInputTitleDescription[formName as keyof typeof SalonInputTitleDescription]
              }
              fields={filteredFields}
              initialValues={salonInputData}
              onChangeFieldValues={(values) =>
                setCurrentValues((prevState) => ({ ...prevState, ...values }))
              }
            />
          );
        })}
        <FormGrid
          formName={t('total')}
          formDescription={
            "These fields are non-editable summaries of information from the 'Total # Service Transactions' and 'Total Service Sales' fields, provided for convenience and clarity."
          }
          fields={totalFields}
          values={totalValues}
        />
        <HistoricalDataModal open={isHistoricalModalOpen} handleClose={handleModalClose} />
      </MainContainer>
      <ControlBar rightButtons={rightButtons} />
      <ConfirmationModal open={isModalOpen} handleClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default GeneralInput;
